import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import './styles/critical.css'; // Critical CSS
import './styles/designTokens.css'; // Design tokens for responsive typography and colors
import './styles/typography.css'; // Typography system with Avenir fonts
import './styles/fontLoading.css'; // Font loading states
import './styles/smoothScrolling.css'; // Smooth scrolling enhancements
import './App.css';
import './styles/reducedMotion.css';
import Header from './components/Header';
import LoadingFallback from './components/LoadingFallback';
import ConsentBanner from './components/ConsentBanner';
import SEO from './components/SEO';
import { AppProvider } from './context/AppContext';
import { getPageMetadata, generateStructuredData } from './utils/seoUtils';
import { initializeGTMDataLayer } from './utils/gtm';
import { initializeGTM } from './utils/gtmIntegration';
import { useRouterTracking, getPageTitleFromRoute, getAdditionalTrackingData } from './hooks/useRouterTracking';
import AgenticChatLauncher from './components/AgenticChatLauncher';
// Temporarily remove PerfToggle to eliminate any scroll monitoring interference
// import PerfToggle from './components/PerfToggle';

// Temporarily disable performance monitors that may interfere with scrolling
// if (process.env.NODE_ENV === 'development') {
//   import('./utils/scrollPerformanceMonitor');
//   import('./utils/passiveListenersAudit');
// }

// Lazy load components that are not needed for initial render
const About = lazy(() => import('./components/About'));
const Footer = lazy(() => import('./components/Footer'));

// Lazy load hero components for code splitting
const MainHero = lazy(() => import('./components/MainHero'));
const ServicesHero = lazy(() => import('./components/ServicesHero'));
const CaseStudiesHero = lazy(() => import('./components/CaseStudiesHero'));

// Lazy load service detail pages
const AIIntegration = lazy(() => import('./pages/services/AIIntegration'));
const IntelligentBusinessNetworks = lazy(() => import('./pages/services/IntelligentBusinessNetworks'));
const MobileApplications = lazy(() => import('./pages/services/MobileApplications'));

// Lazy load case study detail page
const CaseStudyDetail = lazy(() => import('./pages/CaseStudyDetail'));
const FermentsCaseStudy = lazy(() => import('./pages/case-studies/FermentsCaseStudy'));
const CaseStudiesPage = lazy(() => import('./pages/CaseStudiesPage'));
const FermentsDemo = lazy(() => import('./pages/FermentsDemo'));

// Lazy load additional pages
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));

const App = () => {
  // GTM and mobile optimization effects
  useEffect(() => {
    // Initialize new GTM with Consent Mode v2
    initializeGTM();
    
    // Initialize legacy GTM Data Layer (for backward compatibility)
    initializeGTMDataLayer({
      gtmId: window.GTM_CONTAINER_ID || 'GTM-XXXXXXX',
      gaId: window.GA_MEASUREMENT_ID || 'G-429009383',
      customData: {
        app_name: 'Twisted Cloud Solutions',
        app_version: '1.0.0',
        environment: process.env.NODE_ENV
      }
    });

    // Simplified scroll behavior: rely on native scrolling + sticky header; remove legacy optimizers & wheel blockers
    const docEl = document.documentElement;
    const isMobile = window.matchMedia('(max-width: 768px)').matches;
    if (isMobile) {
      docEl.classList.add('mobile-device');
      // Ensure momentum scrolling on iOS
      docEl.style.webkitOverflowScrolling = 'touch';
    }

    // Optional diagnostics: add ?perfdebug=1 to URL to log scroll listener count once
    if (window.location.search.includes('perfdebug=1')) {
      setTimeout(() => {
        // Rough listener introspection (only our known global attaches)
  // debug log removed
      }, 1000);
    }

    return () => {
      if (isMobile) docEl.classList.remove('mobile-device');
    };
  }, []);

  // Router tracking component that must be inside Router context
  const RouterTrackingWrapper = () => {
    // Initialize Router Tracking inside Router context
    useRouterTracking(true, getPageTitleFromRoute, getAdditionalTrackingData);
    return null;
  };

  // Homepage component
  const HomePage = () => {
    const homeMetadata = getPageMetadata('home');
    const organizationSchema = generateStructuredData.organization();
    const websiteSchema = generateStructuredData.website();
    
    return (
      <>
        <SEO
          title={homeMetadata.title}
          description={homeMetadata.description}
          canonical={homeMetadata.canonical}
          keywords={homeMetadata.keywords}
          structuredData={[organizationSchema, websiteSchema]}
        />
        <Header />
        <main className="no-gap-main">
         
          <Suspense fallback={<LoadingFallback />}>
            <MainHero caseStudiesSectionId="case-studies" />
          </Suspense>
          
          <Suspense fallback={<LoadingFallback />}>
            <ServicesHero />
          </Suspense>
   
          <Suspense fallback={<LoadingFallback />}>
            <CaseStudiesHero />
          </Suspense>
      
          <Suspense fallback={<LoadingFallback />}>
            <About />
          </Suspense>
        </main>
        
        <Suspense fallback={<LoadingFallback />}>
          <Footer />
        </Suspense>
      </>
    );
  };

  return (
    <AppProvider>
      <Router>
        <RouterTrackingWrapper />
        <div className="App">
          <ConsentBanner />
          <AgenticChatLauncher />
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/services/ai-integration" element={
              <Suspense fallback={<LoadingFallback />}>
                <AIIntegration />
              </Suspense>
            } />
            <Route path="/services/intelligent-business-networks" element={
              <Suspense fallback={<LoadingFallback />}>
                <IntelligentBusinessNetworks />
              </Suspense>
            } />
            <Route path="/services/mobile-applications" element={
              <Suspense fallback={<LoadingFallback />}>
                <MobileApplications />
              </Suspense>
            } />
            <Route path="/case-studies" element={
              <Suspense fallback={<LoadingFallback />}>
                <CaseStudiesPage />
              </Suspense>
            } />
            <Route path="/case-studies/ferments-ai-sommelier" element={
              <Suspense fallback={<LoadingFallback />}>
                <FermentsCaseStudy />
              </Suspense>
            } />
            <Route path="/case-studies/:slug" element={
              <Suspense fallback={<LoadingFallback />}>
                <CaseStudyDetail />
              </Suspense>
            } />
            <Route path="/ferments-demo" element={
              <Suspense fallback={<LoadingFallback />}>
                <FermentsDemo />
              </Suspense>
            } />
            <Route path="/privacy" element={
              <Suspense fallback={<LoadingFallback />}>
                <PrivacyPolicy />
              </Suspense>
            } />
          </Routes>
          {/* <PerfToggle /> */}
        </div>
      </Router>
    </AppProvider>
  );
};

export default App;
